import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppDropDown from "@toothfairy/shared-ui/AppDropDown";
import AppPlaceholderView from "@toothfairy/shared-ui/AppPlaceholderView";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppTable from "@toothfairy/shared-ui/AppTable";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { View, useWindowDimensions } from "react-native";
import jobsHelper from "../../../../API/Jobs/jobsHelper";
import ToothFairyButton from "../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyChoice from "../../../../Components/ToothFairyComponents/ToothFairyChoice";
import ToothFairyLoadingPlaceholder from "../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { useHistory, useParams } from "../../../../Router";
import DateFilter from "../../../Admin/SubComponents/Leafs/DateFilter";
import { isLargeScreen } from "../../../MainViewContainer";

const JobsList = ({ isFunctionsLoading }) => {
  const { currentTheme } = AppTheme.useTheme();
  const { width } = useWindowDimensions();
  const { getPlanConfig, getWorkspaceEntities } = useWorkspaces();
  const history = useHistory();
  const params = useParams();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const [showChoice, setShowChoice] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [selectedIndex, setSelectedIndex] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [displayedJobs, setDisplayedJobs] = React.useState([]);
  const [selectedJobIds, setSelectedJobIds] = React.useState([]);
  const [showBulkDeleteChoice, setShowBulkDeleteChoice] = React.useState(false);
  const [agentFilter, setAgentFilter] = React.useState("all");
  const [startDate, setStartDate] = React.useState(() => {
    return dayjs().subtract(1, "month").startOf("month").toDate();
  });
  const [endDate, setEndDate] = React.useState(() => {
    return dayjs().add(1, "year").endOf("year").toDate();
  });
  const { t } = useTranslation();
  const AppToasty = AppToast.useToast();
  const { height } = useWindowDimensions();
  const { getActiveWorkspace } = useWorkspaces();
  useEffect(() => {
    const optId = params?.optId;
    if (!optId) return;
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    if (isLoading) return;
    history.replace(`/workspaces/${wsId}/jobs/${optId}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.optId, isLoading, getActiveWorkspace()?.id]);

  const columns = [
    {
      title: t("settings.list.id"),
      width: 0.00001,
    },
    {
      title: t("settings.list.name"),
      width: 0.18,
    },
    {
      title: t("jobs.jobType"),
      width: 0.08,
    },
    {
      title: t("settings.list.agent"),
      width: 0.13,
    },
    {
      title: t("settings.list.prompt"),
      width: 0.13,
    },
    {
      title: t("settings.list.schedule"),
      width: 0.13,
    },
    {
      title: t("settings.list.status"),
      width: 0.1,
    },
    {
      title: t("settings.list.nextRun"),
      width: 0.13,
    },
    {
      title: t("settings.list.actions"),
      isAction: true,
      width: 0.1,
    },
  ];

  const applyFilters = useCallback(() => {
    let filtered = [...(state?.jobs || [])];

    if (agentFilter && agentFilter !== "all") {
      filtered = filtered.filter((item) => item.agentID === agentFilter);
    }

    if (startDate) {
      filtered = filtered.filter((item) => {
        const jobDate = item.startDate ? new Date(item.startDate) : new Date(0);
        return jobDate >= startDate;
      });
    }

    if (endDate) {
      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);
      filtered = filtered.filter((item) => {
        const jobDate = item.endDate ? new Date(item.endDate) : new Date();
        return jobDate <= endOfDay;
      });
    }

    setDisplayedJobs(filtered);
  }, [state?.jobs, agentFilter, startDate, endDate]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleJobItemSelection = ({ id, item, index }) => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId || !id) return;
    history.push(`/workspaces/${wsId}/jobs/${id}`);
  };

  const loadJobs = async (workspaceID) => {
    try {
      setIsLoading(true);

      let resultJobs = await jobsHelper?.getJobsByWorkspaceID(workspaceID);

      resultJobs = resultJobs?.data?.cronJobsByWorkspace?.items?.filter(
        (u) => u?._deleted != true
      );

      dispatch("SET_JOBS", resultJobs || []);
    } catch (error) {
      console.error("Error loading jobs:", error);
      dispatch("SET_JOBS", []);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (getActiveWorkspace()?.id) {
      loadJobs(getActiveWorkspace()?.id);
    }
  }, [getActiveWorkspace()?.id]);

  const handleJobItemDeletion = async (item, index) => {
    try {
      setShowChoice(false);
      const _id = selectedItem?.id;
      if (_id) {
        const result = await jobsHelper.deleteJob(_id);

        const workspaceID = getActiveWorkspace()?.id;
        if (workspaceID) {
          await loadJobs(workspaceID);
        }

        AppToasty.show(t("jobs.jobDeletionSuccessCaption"), {
          type: "success",
        });
        setSelectedIndex(null);
        setSelectedItem(null);
      } else {
        throw new Error(t("settings.list.noItemSelected"));
      }
    } catch (error) {
      console.log(error);
      AppToasty.show(t("jobs.jobDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };

  const handleAgentFilterChange = (agentId) => {
    setAgentFilter(agentId);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const handleBulkDeletion = async () => {
    try {
      for (const id of selectedJobIds) {
        await jobsHelper.deleteJob(id);
      }
      const workspaceID = getActiveWorkspace()?.id;
      if (workspaceID) {
        await loadJobs(workspaceID);
      }
      setSelectedJobIds([]);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("jobs.jobDeletionSuccessCaption"), {
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setShowBulkDeleteChoice(false);
      AppToasty.show(t("jobs.jobDeletionErrorCaption"), {
        type: "danger",
      });
    }
  };

  const handleCreation = () => {
    const wsId = getActiveWorkspace()?.id;
    if (!wsId) return;
    history.push(`/workspaces/${wsId}/jobs/new`);
  };

  const isItemActionable = (item) => {
    const _item = item?.item;
    if (_item) return true;
    else return false;
  };

  const getAgentName = (agentID) => {
    const agent = state?.agents?.find((a) => a.id === agentID);
    return agent?.label || t("settings.list.unknownAgent");
  };

  const getPromptName = (promptID) => {
    const prompt = state?.customPrompts?.find((p) => p.id === promptID);
    return prompt?.interpolationString || t("settings.list.unknownPrompt");
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return t("settings.list.notSet");
    if (typeof schedule === "string") {
      schedule = JSON.parse(schedule);
    }

    const freq = schedule.frequency || "CUSTOM";
    const hour = schedule.hour !== undefined ? schedule.hour : 0;
    const minute = schedule.minute !== undefined ? schedule.minute : 0;

    if (freq === "CUSTOM" && schedule.cronExpression) {
      return schedule.cronExpression;
    }
    const timeStr = `${hour?.toString()?.padStart(2, "0")}:${minute
      ?.toString()
      ?.padStart(2, "0")}`;

    switch (freq) {
      case "MINUTES":
        return t("settings.list.everyMinute", {
          count: schedule.interval || 1,
        });
      case "HOURLY":
        return t("settings.list.everyHour", { count: schedule.interval || 1 });
      case "DAILY":
        return t("settings.list.dailyAt", { time: timeStr });
      case "WEEKLY":
        // dayOfWeek is stored Monday=0 ... Sunday=6 (backend convention)
        const days = [
          t("settings.list.monday"),
          t("settings.list.tuesday"),
          t("settings.list.wednesday"),
          t("settings.list.thursday"),
          t("settings.list.friday"),
          t("settings.list.saturday"),
          t("settings.list.sunday"),
        ];
        const dayName = days[schedule.dayOfWeek] || t("settings.list.monday");
        return t("settings.list.weeklyOn", { day: dayName, time: timeStr });
      case "MONTHLY":
        return t("settings.list.monthlyOn", {
          day: schedule.dayOfMonth || 1,
          time: timeStr,
        });
      case "YEARLY":
        return t("settings.list.yearlyAt", { time: timeStr });
      default:
        return freq;
    }
  };

  const formatNextRun = (nextRunAt) => {
    if (!nextRunAt) return t("settings.list.notScheduled");
    const date = new Date(nextRunAt);
    return date.toLocaleString();
  };

  const _coreChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancel")}
        caption={t("settings.list.deleteJobConfirmation")}
        subCaption={t("settings.list.deleteJobConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowChoice(false)}
        onConfirmButtonPress={() => handleJobItemDeletion()}
        setShowChoice={setShowChoice}
        showChoice={showChoice}
      />
    );
  }, [setShowChoice, showChoice, selectedItem, setSelectedItem]);

  const _bulkDeleteChoice = useMemo(() => {
    return (
      <ToothFairyChoice
        cancelCaption={t("settings.list.cancelConfirmation")}
        caption={t("settings.list.deleteSelectedConfirmation", {
          defaultValue: t("settings.list.deleteJobConfirmation"),
        })}
        subCaption={t("settings.list.deleteJobConfirmationMessage")}
        confirmCaption={t("settings.list.delete")}
        onCancelButtonPress={() => setShowBulkDeleteChoice(false)}
        onConfirmButtonPress={handleBulkDeletion}
        setShowChoice={setShowBulkDeleteChoice}
        showChoice={showBulkDeleteChoice}
      />
    );
  }, [showBulkDeleteChoice, selectedJobIds]);

  return (
    <View
      style={{
        minWidth: "100%",
        paddingLeft: 10,
        flex: 1,
        overflow: isLoading && "hidden",
      }}
    >
      <>
        {isLoading ? (
          <ToothFairyLoadingPlaceholder
            backgroundColor={currentTheme?.theme?.softBackgroundColor}
            visible={true}
            containerStyle={{
              backgroundColor: currentTheme?.theme?.transparent,
              minHeight: 250,
            }}
            caption={t("settings.list.loadingJobs")}
          />
        ) : (
          <>
            {displayedJobs.length > 0 ? (
              <AppTable
                isSelectable
                showPagination
                itemsPerPage={50}
                hideSearch={false}
                searchPlaceholder={t("settings.toolbar.searchJobs")}
                onSelectionChange={(selectedItems) => {
                  setSelectedJobIds(selectedItems?.map((u) => u?.id) || []);
                }}
                bulkActions={[
                  {
                    id: "DELETE",
                    label: t("settings.list.delete"),
                    icon: AppAssets.icons.delete,
                    isDestructive: true,
                  },
                ]}
                onBulkActionPress={(id) => {
                  if (id === "DELETE" && selectedJobIds?.length > 0)
                    setShowBulkDeleteChoice(true);
                }}
                actionButtons
                mapIconWithStatus={(item) => {
                  if (item?.status === "ACTIVE" || item?.status === "RUNNING")
                    return AppAssets.icons.reload;
                  else if (item?.status === "SENSING")
                    return AppAssets.icons.search;
                  else if (item?.status === "PLANNING")
                    return AppAssets.icons.brain;
                  else if (item?.status === "ACTING")
                    return AppAssets.icons.rocket;
                  else if (item?.status === "PAUSED")
                    return AppAssets.icons.pause;
                  else if (item?.status === "FAILED")
                    return AppAssets.icons.error;
                  else return AppAssets.icons.clock;
                }}
                mainActionLabel={t("settings.list.view")}
                showDropDownMenuForActions
                onClickMainAction={(item, index) => {
                  handleJobItemSelection({
                    id: item?.id,
                    item: item,
                    index: index,
                  });
                }}
                actionButtonStyle={{
                  minWidth: 20,
                }}
                onActionItemPress={async (id, item, index) => {
                  if (id === "EDIT") {
                    handleJobItemSelection({
                      id: item?.id,
                      item: item,
                      index: index,
                    });
                  } else if (id === "DELETE") {
                    setSelectedItem(item);
                    setSelectedIndex(index);
                    setShowChoice(true);
                  }
                }}
                actions={[
                  {
                    label: t("settings.list.edit"),
                    id: "EDIT",
                    icon: AppAssets.icons.edit,
                  },
                  {
                    label: t("settings.list.delete"),
                    id: "DELETE",
                    icon: AppAssets.icons.delete,
                  },
                ]}
                isItemActionable={(item) =>
                  isItemActionable({
                    item: item,
                  })
                }
                style={{
                  zIndex: -1,
                  marginTop: 15,
                  height:
                    displayedJobs?.length >= 50 ? height - 95 : height - 75,
                  paddingRight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                }}
                headers={columns}
                items={displayedJobs
                  ?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""))
                  ?.map((u) => {
                    return {
                      id: u?.id || " ",
                      name: u?.name || " ",
                      type:
                        u?.type === "ROUTINE"
                          ? t("jobs.routine")
                          : t("jobs.task"),
                      agent: getAgentName(u?.agentID),
                      prompt:
                        u?.forcedPrompt || getPromptName(u?.customPromptID),
                      schedule: formatSchedule(u?.schedule),
                      status: u?.status || "PENDING",
                      nextRun: formatNextRun(u?.nextRunAt),
                      actions: " ",
                    };
                  })}
              >
                <AppDropDown
                  showTextInputLabel
                  label={t("settings.toolbar.agent")}
                  inputLabelStyle={{
                    marginBottom: 0,
                    marginLeft: 5,
                    marginRight: 10,
                  }}
                  dropDownStyle={{
                    minWidth: 184,
                    maxWidth: 184,
                    textAlign: "flex-start",
                    borderColor: currentTheme?.theme?.border_color,
                  }}
                  wrapperContainer={{
                    flexDirection: "row",
                  }}
                  inputLabelFontWeight={"normal"}
                  isElevated={false}
                  items={[
                    { label: t("settings.toolbar.noFilter"), value: "all" },
                    ...(
                      state?.agents?.filter(
                        (agent) => agent?.mode !== "voice"
                      ) || []
                    ).map((agent) => ({
                      label:
                        agent.label ||
                        agent.name ||
                        t("settings.toolbar.unknown"),
                      value: agent.id,
                    })),
                  ]}
                  selectedValue={agentFilter}
                  onValueChange={(itemValue) => {
                    handleAgentFilterChange(itemValue);
                  }}
                />
                <DateFilter
                  startDate={startDate}
                  endDate={endDate}
                  onUpdateStartDate={handleStartDateChange}
                  onUpdateEndDate={handleEndDateChange}
                  paddingBottom={0}
                />
                <ToothFairyButton
                  title={t("settings.toolbar.createJob")}
                  onPress={handleCreation}
                />
              </AppTable>
            ) : (
              <AppPlaceholderView
                wrapperStyle={{
                  width: "100%",
                  height: "100%",
                  marginVertical: 80,
                  marginHorizontal: "auto",
                  flex: 1,
                }}
                size={30}
                icon={AppAssets.icons.days}
                mainCaption={t("settings.list.noJobsFound")}
                subCaption={t("settings.list.noJobsFoundSub")}
                actionLabel={t("settings.toolbar.createJob")}
                onActionPress={handleCreation}
              />
            )}
          </>
        )}
      </>
      {_coreChoice}
      {_bulkDeleteChoice}
    </View>
  );
};

export default JobsList;
